import { useMemo } from "react";
import type { MaterialChar, Task } from "@xinghai/shared";
import { Icon, Tag } from "@xinghai/ui";
import type { MaterialWithPerception } from "../../lib/api";
import { TASK_HINT, displayPinyin, stripTone } from "./taskMeta";

/**
 * Split `material.chars` across the sentences of a passage. `chars` covers the
 * syllabic characters of `text` in order and skips punctuation, so each
 * sentence takes as many entries as it has Han characters.
 */
export function charsForSentences(
  chars: readonly MaterialChar[],
  sentences: readonly string[],
): MaterialChar[][] {
  let cursor = 0;
  return sentences.map((s) => {
    const n = (s.match(/\p{Script=Han}/gu) ?? []).length;
    const slice = chars.slice(cursor, cursor + n);
    cursor += n;
    return slice;
  });
}

/** Split a passage into the sentences the student records one by one. */
export function splitSentences(material: MaterialWithPerception): string[] {
  if (material.sentences?.length) return material.sentences;
  const parts = material.text
    .split(/(?<=[。！？!?；;\n])/u)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  return parts.length > 0 ? parts : [material.text];
}

// --------------------------------------------------------------------------- //
// text layout: chars[] carries the pinyin, text carries the punctuation
// --------------------------------------------------------------------------- //

/** Marks that hang on the end of the preceding character. */
const TRAILING = new Set([
  "，", "。", "；", "：", "、", "！", "？", "”", "’", "》", "〉", "」", "』", "】", "〕", "）",
  "…", "—", ",", ".", ";", ":", "!", "?", ")", "]", "》",
]);
/** Marks that open a run and belong to the character that follows. */
const LEADING = new Set(["《", "〈", "“", "‘", "（", "「", "『", "【", "〔", "(", "["]);
/** A visual line ends after these. */
const BREAK_AFTER = new Set(["。", "！", "？", "；", "!", "?", ";"]);

/** One character with its locked pinyin, plus any punctuation glued to it. */
export interface CharCell {
  kind: "char";
  char: string;
  pinyin: string;
  /** Index into `material.chars`. */
  index: number;
  /** 《 “ （ … rendered before the character, same cell. */
  lead?: string;
  /** ，。！ … rendered after the character, same cell. */
  trail?: string;
}

/** Punctuation with no character to attach to (rare: a line that starts with it). */
export interface PunctCell {
  kind: "punct";
  text: string;
}

export type TextCell = CharCell | PunctCell;

/**
 * Walk `text` and `chars` together. `chars` covers the spoken units (Han
 * characters, or letter strings for 音素 items) in text order; everything else
 * in `text` is punctuation or whitespace.
 *
 * Punctuation never starts a line: closing marks ride along with the character
 * before them, opening marks with the character after. A new visual line starts
 * after 。！？； and after every newline, so a poem keeps one 句 per line.
 */
export function layoutMaterialText(
  text: string,
  chars: readonly MaterialChar[],
): TextCell[][] {
  if (chars.length === 0) return [];
  if (!text) return [chars.map((c, index) => ({ kind: "char", char: c.char, pinyin: c.pinyin_locked, index }))];

  const lines: TextCell[][] = [[]];
  let line = lines[0]!;
  let ci = 0;
  let pendingLead = "";
  let i = 0;

  const newline = () => {
    if (line.length > 0) {
      line = [];
      lines.push(line);
    }
  };

  while (i < text.length) {
    const next = chars[ci];
    if (next && next.char.length > 0 && text.startsWith(next.char, i)) {
      const cell: CharCell = {
        kind: "char",
        char: next.char,
        pinyin: next.pinyin_locked,
        index: ci,
      };
      if (pendingLead) {
        cell.lead = pendingLead;
        pendingLead = "";
      }
      line.push(cell);
      i += next.char.length;
      ci += 1;
      continue;
    }

    const ch = text[i]!;
    i += 1;

    if (ch === "\n" || ch === "\r") {
      newline();
      continue;
    }
    if (/\s/.test(ch)) continue; // spaces only separate; the layout already gaps

    if (LEADING.has(ch)) {
      pendingLead += ch;
      continue;
    }

    const last = line[line.length - 1];
    if (TRAILING.has(ch) && last && last.kind === "char") {
      last.trail = (last.trail ?? "") + ch;
    } else {
      line.push({ kind: "punct", text: ch });
    }
    if (BREAK_AFTER.has(ch)) newline();
  }

  // Characters the text did not account for still have to be readable.
  for (; ci < chars.length; ci += 1) {
    const c = chars[ci]!;
    line.push({ kind: "char", char: c.char, pinyin: c.pinyin_locked, index: ci });
  }

  return lines.filter((l) => l.length > 0);
}

interface PinyinTextProps {
  chars: readonly MaterialChar[];
  /** The material text, so punctuation and line breaks survive. */
  text?: string;
  /** 红线 14.1 — the singing screen shows pinyin without any tone. */
  hideTone?: boolean;
  size?: "md" | "lg";
  /** Fallback when the material has no per-char pinyin. */
  fallbackText?: string;
}

/** Characters with their locked pinyin underneath (红线 14.5). */
export function PinyinText({
  chars,
  text,
  hideTone = false,
  size = "md",
  fallbackText,
}: PinyinTextProps) {
  const lines = useMemo(() => layoutMaterialText(text ?? "", chars), [text, chars]);
  if (chars.length === 0) return <p className="xz-material__plain">{fallbackText}</p>;

  const py = (raw: string) => (hideTone ? stripTone(raw) : displayPinyin(raw));

  return (
    <div className={`xz-pinyin xz-pinyin--${size}`}>
      {lines.map((cells, li) => (
        <p className="xz-pinyin__line" key={li}>
          {cells.map((cell, i) =>
            cell.kind === "punct" ? (
              <span className="xz-pinyin__loose" key={`p-${i}`} aria-hidden="true">
                {cell.text}
              </span>
            ) : (
              <span className="xz-pinyin__unit" key={`${cell.char}-${cell.index}-${i}`}>
                {cell.lead && <span className="xz-pinyin__punct">{cell.lead}</span>}
                <span className="xz-pinyin__glyphs">
                  <span className="xz-pinyin__char">{cell.char}</span>
                  <span className="xz-pinyin__py">{py(cell.pinyin)}</span>
                </span>
                {cell.trail && <span className="xz-pinyin__punct">{cell.trail}</span>}
              </span>
            ),
          )}
        </p>
      ))}
    </div>
  );
}

export interface MaterialDisplayProps {
  material: MaterialWithPerception;
  task: Task;
  /** T4: index of the sentence being recorded. */
  segmentIndex?: number;
  /** T4: sentences already recorded. */
  doneSegments?: ReadonlySet<number>;
  onSelectSegment?: (index: number) => void;
}

/**
 * The material half of the record screen (docs/UX.md 2).
 * Everything is sized for a 375px phone held in one hand.
 */
export function MaterialDisplay({
  material,
  task,
  segmentIndex = 0,
  doneSegments,
  onSelectSegment,
}: MaterialDisplayProps) {
  const sentences = useMemo(() => splitSentences(material), [material]);
  const hint = material.hint ?? TASK_HINT[task] ?? null;
  const singing = task === "T6_singing" || material.domain === "singing";

  if (task === "T2_diagnostic") {
    const first = material.chars[0];
    return (
      <section className="xz-material xz-material--single" aria-label="要读的字">
        <span className="xz-material__char" lang="zh-CN">
          {first?.char ?? material.text}
        </span>
        <span className="xz-material__pinyin">{displayPinyin(first?.pinyin_locked ?? "")}</span>
        {hint && <p className="xz-material__hint">{hint}</p>}
      </section>
    );
  }

  if (task === "T4_passage") {
    const perSentence = charsForSentences(material.chars, sentences);
    const currentChars = perSentence[segmentIndex] ?? [];
    return (
      <section className="xz-material" aria-label="要读的段落">
        {currentChars.length > 0 && (
          <PinyinText
            chars={currentChars}
            text={sentences[segmentIndex] ?? ""}
            fallbackText={sentences[segmentIndex] ?? ""}
          />
        )}
        <ol className="xz-sentences">
          {sentences.map((s, i) => {
            const done = doneSegments?.has(i) ?? false;
            const current = i === segmentIndex;
            return (
              <li
                key={`${i}-${s.slice(0, 6)}`}
                className={[
                  "xz-sentences__item",
                  current ? "is-current" : "",
                  done ? "is-done" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                aria-current={current ? "step" : undefined}
              >
                <button
                  type="button"
                  className="xz-sentences__btn"
                  onClick={onSelectSegment ? () => onSelectSegment(i) : undefined}
                  disabled={!onSelectSegment}
                >
                  <span className="xz-sentences__state" aria-hidden="true">
                    {done ? <Icon id="ui-check-circle" size={16} /> : i + 1}
                  </span>
                  <span className="xz-sentences__text">{s}</span>
                  <span className="xz-sr-only">
                    {done ? "已录完" : current ? "当前这句" : "还没录"}
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
        {hint && <p className="xz-material__hint">{hint}</p>}
      </section>
    );
  }

  if (task === "T5_lyric_reading" || task === "T6_singing") {
    return (
      <section className="xz-material" aria-label={singing ? "要唱的歌词" : "要读的歌词"}>
        {task === "T6_singing" && (
          <Tag tone="info">
            <Icon id="ui-speaking" size={16} /> 这一条要唱出来
          </Tag>
        )}
        <PinyinText
          chars={material.chars}
          text={material.text}
          hideTone={task === "T6_singing"}
          size="lg"
          fallbackText={material.text}
        />
        {hint && <p className="xz-material__hint">{hint}</p>}
      </section>
    );
  }

  // T3 绕口令 and everything else: the full text with locked pinyin.
  return (
    <section className="xz-material" aria-label="要读的内容">
      <PinyinText chars={material.chars} text={material.text} fallbackText={material.text} />
      {hint && <p className="xz-material__hint">{hint}</p>}
    </section>
  );
}
